/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { baseColumnProps, isBaseColumnPropertyVisible } from '../BaseColumn';
import { defaultValue } from './TreeColumn';

const TreeColumnManager: ComponentManagerFn<'TreeColumn'> = (
  platform: Platform,
): ComponentManager<'TreeColumn'> => {
  return {
    ...baseComponentManager<'TreeColumn'>(platform, 'TreeColumn'),
    type: 'TreeColumn',
    name: 'Tree Column',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'TriggerOnCellClick',
          'TriggerOnTreeColumnExpand',
          'TriggerOnIconClick',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      ...baseColumnProps,
      viewAttribute: {
        ...(baseColumnProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: ({ props }) => {
          return ['String'];
        },
      },
      notExpand: {
        label: 'No Expansion On Cell Active',
        type: 'boolean',
      },
      isLeaf: {
        label: 'Is Leaf',
        type: 'ViewAttributeChooser',
        getAttributeDataTypes: ({ props }) => {
          return ['Boolean'];
        },
        aliasType: 'STORE',
      },
      nodeIdAttribute: {
        label: 'Node Id attribute',
        type: 'ViewAttributeChooser',
        getAttributeDataTypes: ({ props }) => {
          return ['String', 'Integer'];
        },
        aliasType: 'STORE',
      },
      parentNodeIdAttribute: {
        label: 'Parent Node Id Attribute',
        type: 'ViewAttributeChooser',
        getAttributeDataTypes: ({ props }) => {
          return ['String', 'Integer'];
        },
        aliasType: 'STORE',
      },
      endIcon: {
        label: 'End Icon',
        type: 'expression',
        returnType: async () => 'string',
        helpText:
          'This icon will be shown on right side of the text and you can have trigger on this icon click',
      },
      initialExpanded: {
        label: 'Expand all nodes initially',
        type: 'boolean',
      },
    },
    isPropertyVisible(prop, props, { appUid, itemId, pageId }): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }
      switch (prop) {
        case 'toolTip':
        case 'toolTipPosition':
        case 'toolTipMaxWidth':
        case 'alignment':
          return false;
        default:
          return true;
      }
    },
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    category: 'Data Grid',
    icon: { iconName: 'folder-tree' },
    description: `**Overview**<br/>
The Tree Column transforms a standard Data Grid into a Hierarchical Tree Grid. It visually nests rows to represent parent-child relationships, such as a File System structure, an Organizational Chart, or a Bill of Materials.
Typically placed as the first column in the grid, it renders the necessary indentation and expand/collapse arrows (>) next to the text label.

**1. Defining the Hierarchy**<br/>
Unlike some components that expect nested JSON data (e.g., \`children: []\`), the Tree Column works with **Flat Data (Adjacency List)**. It reconstructs the tree structure dynamically by linking records via ID.
To make this work, you must map three key attributes:
* **Node Id Attribute:** The unique identifier for the row (e.g., "EmployeeID").
* **Parent Node Id Attribute:** The identifier of the parent record (e.g., "ManagerID").
    * **Logic:** If ManagerID is empty (null), the row is a "Root" node. If ManagerID matches another row's EmployeeID, it becomes a child of that row.
* **View Attribute:** The text label to display for the node (e.g., "EmployeeName").

**2. Expansion Behavior**<br/>
You can control how the tree expands and identifies folders vs. files.
* **Is Leaf:** A boolean attribute from your Data Store.
    * **True:** The row is a "Leaf" (End Node). No expand arrow is shown.
    * **False:** The row is a "Branch" (Folder). An expand arrow is shown.
* **Expand All Nodes Initially:** If enabled, the grid loads with the entire tree fully open.
* **No Expansion On Cell Active:**
    * **Enabled:** Clicking the text of the cell does not expand the row; the user must explicitly click the small arrow icon.
    * **Disabled:** Clicking anywhere in the cell expands/collapses the row.

**3. Interaction & Logic**<br/>
The Tree Column supports specific triggers to handle the complexity of hierarchy.
* **TriggerOnTreeColumnExpand:** Fires when a user expands a node.
    * **Use Case (Lazy Loading):** If your dataset is massive, you can load only the root nodes first. When the user expands a node, use this trigger to fetch the child records from the server on-demand.
* **End Icon:** An expression to render a secondary icon on the right side of the text (e.g., a "Menu" or "Info" icon).
* **TriggerOnIconClick:** Fires when this specific end icon is clicked.

**4. Best Practices**<br/>
* **Sorting:** Standard sorting often breaks the visual hierarchy of a tree. It is usually best to rely on the natural order of the data or sort only within siblings.
* **First Column:** Always place the Tree Column as the first (leftmost) column in your grid definition for the most intuitive user experience.`,
  };
};

export default TreeColumnManager;
